// @givmo/compliance — a form Givmo prepares, and its filing case's checklist, decoded (CCM-R1-DOCS
// web half; the backend's `CCM-R1-DOCS` and `CCM-R1-DOCS-READS`).
//
// ══ WHAT THIS FILE READS ═══════════════════════════════════════════════════════════════════════
//
// Every read the filing workbench and the panel's "This year's filing" take, each tolerantly off
// `unknown`, in `compliance-decode.ts`'s style:
//
//   • the forms Givmo prepares, `…/form-adapters`;
//   • a filing's cases, `…/filing-cases?obligation_id=` (CCM-R1-FILING-CASE);
//   • a case's preparations, `…/filing-cases/{case}/form-preparations` (CCM-R1-DOCS-READS: the READ
//     way to a case's form — starting one is a write a viewer may not make);
//   • one preparation, `…/form-preparations/{id}`: every item in the form's own order and words,
//     where it stands, what goes on the form and from which record, and each preview page's draw
//     scale (CCM-R1-DOCS-READS); and what the form itself prints beside its items, in its own
//     words (CCM-R1-FORM-CATALOG): its title, the page holding its checklist, the questions it
//     prints over several items, and its fee chart (CCM-R1-DOCS-PRINTED-WEB);
//   • a recorded version, `…/form-preparations/{id}/versions`, and the link to its exact bytes;
//   • the case's checklist, `…/filing-cases/{case}/checklist`;
//   • the case's signing requests, `…/signing-requests?filing_case_id=` (read only here: whether the
//     form is out for signature, and who signed it).
//
// ⚠ AN UNREADABLE VALUE NEVER RESOLVES TOWARDS THE MORE CONFIDENT ANSWER. An item whose kind this
// build does not know is shown, read-only, with what the form says for it; an item whose state is
// unknown reads as waiting when the server says it waits; an answer whose kind is unknown is no
// answer. Nothing here invents a value the wire did not carry.

import { extractList } from '@givmo/api-client/list';

// ── Coercion primitives ────────────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function rect(value: unknown): Rect | undefined {
  if (!Array.isArray(value) || value.length !== 4) return undefined;
  const numbers = value.map(optionalNumber);
  if (numbers.some((n) => n === undefined)) return undefined;
  const [x0, y0, x1, y1] = numbers as number[];
  return x0! < x1! && y0! < y1! ? [x0!, y0!, x1!, y1!] : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** A rectangle in PDF points from the page's bottom left: `[x0, y0, x1, y1]`. */
export type Rect = readonly [number, number, number, number];

// ── The forms Givmo prepares ───────────────────────────────────────────────────────────────────

/** One agency form Givmo prepares from the agency's template, and the rule it is filed under. */
export interface FormAdapter {
  adapterKey: string;
  adapterVersion: number;
  jurisdictionCode: string;
  ruleId: string;
  formNumber: string;
  shortName?: string;
  pageCount: number;
  signaturePlaces: number;
  /** The day Givmo took the form's template (served): L6 names it under an earlier year's form. */
  templateAccessedOn?: string;
}

export function extractFormAdapters(body: unknown): FormAdapter[] {
  return extractList<unknown>(body).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const adapterKey = optionalStr(raw.adapter_key);
    const adapterVersion = optionalInt(raw.adapter_version);
    const jurisdictionCode = optionalStr(raw.jurisdiction_code);
    const ruleId = optionalStr(raw.rule_id);
    if (!adapterKey || adapterVersion === undefined || !jurisdictionCode || !ruleId) return [];
    return [
      {
        adapterKey,
        adapterVersion,
        jurisdictionCode,
        ruleId,
        formNumber: str(raw.form_number) || adapterKey,
        shortName: optionalStr(raw.short_name),
        pageCount: optionalInt(raw.page_count) ?? 0,
        signaturePlaces: optionalInt(raw.signature_places) ?? 0,
        ...(optionalStr(raw.template_accessed_on) !== undefined
          ? { templateAccessedOn: optionalStr(raw.template_accessed_on) }
          : {}),
      },
    ];
  });
}

/** The form Givmo prepares for a filing's rule, if any (matched on the rule's own identity). */
export function adapterForRule(
  adapters: readonly FormAdapter[],
  jurisdictionCode: string,
  ruleId: string,
): FormAdapter | undefined {
  return adapters.find(
    (adapter) => adapter.jurisdictionCode === jurisdictionCode && adapter.ruleId === ruleId,
  );
}

// ── Filing cases ───────────────────────────────────────────────────────────────────────────────

export type FilingCaseKind = 'initial' | 'renewal' | 'amendment' | 'withdrawal';

/** One filing's case: the record that holds its documents (CCM-R1-FILING-CASE). */
export interface FilingCase {
  caseId: string;
  obligationId: string;
  kind?: FilingCaseKind;
  /** Only an `open` case is one a form is prepared in; anything unreadable is not open. */
  open: boolean;
  /** The durable cycle the case files for — match a case to its year by THIS, never `cycleKey`. */
  renewalCycleId?: string;
  createdAt?: string;
}

function decodeFilingCase(raw: unknown): FilingCase | undefined {
  if (!isRecord(raw)) return undefined;
  const caseId = optionalStr(raw.case_id);
  const obligationId = optionalStr(raw.obligation_id);
  if (!caseId || !obligationId) return undefined;
  return {
    caseId,
    obligationId,
    kind: oneOf(raw.kind, ['initial', 'renewal', 'amendment', 'withdrawal'] as const),
    open: raw.state === 'open',
    renewalCycleId: optionalStr(raw.renewal_cycle_id),
    createdAt: optionalStr(raw.created_at),
  };
}

export function extractFilingCases(body: unknown): FilingCase[] {
  return extractList<unknown>(body).flatMap((raw) => {
    const decoded = decodeFilingCase(raw);
    return decoded === undefined ? [] : [decoded];
  });
}

export function decodeOpenedFilingCase(body: unknown): FilingCase | undefined {
  return decodeFilingCase(body);
}

/**
 * One case as `GET …/filing-cases/{case_id}` answers it: `{ case, documents }`
 * (`ComplianceFilingCaseDetailOut`), never the bare case the POST answers. A bare case here is
 * not this read's shape, so it decodes to nothing.
 */
export function decodeFilingCaseDetail(body: unknown): FilingCase | undefined {
  return isRecord(body) ? decodeFilingCase(body.case) : undefined;
}

// ── A case's preparations (CCM-R1-DOCS-READS) ──────────────────────────────────────────────────

export interface PreparationSummary {
  preparationId: string;
  filingCaseId: string;
  documentId: string;
  adapterKey: string;
  createdByUserId?: string;
  createdAt?: string;
}

export function extractPreparationSummaries(body: unknown): PreparationSummary[] {
  return extractList<unknown>(body).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const preparationId = optionalStr(raw.preparation_id);
    const filingCaseId = optionalStr(raw.filing_case_id);
    const documentId = optionalStr(raw.document_id);
    const adapterKey = optionalStr(raw.adapter_key);
    if (!preparationId || !filingCaseId || !documentId || !adapterKey) return [];
    return [
      {
        preparationId,
        filingCaseId,
        documentId,
        adapterKey,
        createdByUserId: optionalStr(raw.created_by_user_id),
        createdAt: optionalStr(raw.created_at),
      },
    ];
  });
}

// ── A preparation ──────────────────────────────────────────────────────────────────────────────

/** What kind of answer an item takes, and so how it is asked (the backend's `ItemKind`). */
export type FormItemKind =
  | 'text'
  | 'lines'
  | 'table'
  | 'amount'
  | 'month'
  | 'month_year'
  | 'yes_no_written'
  | 'checked'
  | 'statement'
  | 'unknown';

const ITEM_KINDS = [
  'text',
  'lines',
  'table',
  'amount',
  'month',
  'month_year',
  'yes_no_written',
  'checked',
  'statement',
] as const;

/** Where one item stands (the backend's `ItemState`). */
export type FormItemState =
  | 'from_record'
  | 'records_differ'
  | 'entered'
  | 'doesnt_apply'
  | 'route'
  | 'same_as'
  | 'needs_input'
  | 'not_printable'
  | 'does_not_fit'
  | 'not_asked'
  | 'optional_empty'
  | 'statement'
  | 'unknown';

const ITEM_STATES = [
  'from_record',
  'records_differ',
  'entered',
  'doesnt_apply',
  'route',
  'same_as',
  'needs_input',
  'not_printable',
  'does_not_fit',
  'not_asked',
  'optional_empty',
  'statement',
] as const;

export type AnswerKind = 'value' | 'doesnt_apply' | 'route' | 'same_as' | 'choose_source' | 'clear';

const ANSWER_KINDS = [
  'value',
  'doesnt_apply',
  'route',
  'same_as',
  'choose_source',
  'clear',
] as const;

/** The records an item's value can come from (the backend's `SourceKind`). */
export type SourceKind =
  | 'ein_on_file'
  | 'organization_profile'
  | 'irs_exempt_list'
  | 'compliance_profile'
  | 'filing_cycle';

const SOURCE_KINDS = [
  'ein_on_file',
  'organization_profile',
  'irs_exempt_list',
  'compliance_profile',
  'filing_cycle',
] as const;

export type SourceField =
  | 'ein'
  | 'name'
  | 'phone'
  | 'email'
  | 'mission'
  | 'address'
  | 'fiscal_year_end_month'
  | 'period_end_month'
  | 'period_end_year';

const SOURCE_FIELDS = [
  'ein',
  'name',
  'phone',
  'email',
  'mission',
  'address',
  'fiscal_year_end_month',
  'period_end_month',
  'period_end_year',
] as const;

/** The form's (or the agency's) own words, verbatim, and where they are printed. */
export interface FormQuote {
  text: string;
  cite: string;
}

function decodeQuote(raw: unknown): FormQuote | undefined {
  if (!isRecord(raw)) return undefined;
  const text = optionalStr(raw.text);
  if (!text) return undefined;
  return { text, cite: str(raw.cite) };
}

export interface FormPlace {
  page: number;
  rect: Rect;
}

export interface FormChoice {
  key: string;
  label: string;
}

/** An answer the form's instructions offer ("see attached list"), and what it writes. */
export interface FormRoute {
  key: string;
  writes: string;
}

/** A record an item's value comes from, with its own date where it has one. */
export interface FormSource {
  kind: SourceKind;
  field: SourceField;
  asOf?: string;
  value?: string;
  valueSha256?: string;
}

/** A record the form fills an item from, as the form's adapter declares it (CCM-R1-DOCS-READS):
 * named whether or not it holds a value now. */
export interface FormSourceRef {
  kind: SourceKind;
  field: SourceField;
}

function decodeSourceRef(raw: unknown): FormSourceRef | undefined {
  if (!isRecord(raw)) return undefined;
  const kind = oneOf(raw.kind, SOURCE_KINDS);
  const field = oneOf(raw.field, SOURCE_FIELDS);
  return kind !== undefined && field !== undefined ? { kind, field } : undefined;
}

function decodeSource(raw: unknown): FormSource | undefined {
  if (!isRecord(raw)) return undefined;
  const kind = oneOf(raw.kind, SOURCE_KINDS);
  const field = oneOf(raw.field, SOURCE_FIELDS);
  if (kind === undefined || field === undefined) return undefined;
  return {
    kind,
    field,
    asOf: optionalStr(raw.as_of),
    value: optionalStr(raw.value),
    valueSha256: optionalStr(raw.value_sha256),
  };
}

/** The organization's newest answer to an item: what it is, who gave it and when. */
export interface FormAnswer {
  kind: AnswerKind;
  value: unknown;
  answeredByUserId?: string;
  /** Givmo's filing desk gave it (D-CCM-70's marker): its name slot reads N12 (CCM-R1-DESK-WEB). */
  answeredByGivmoDesk?: boolean;
  answeredAt?: string;
}

function decodeAnswer(raw: unknown): FormAnswer | undefined {
  if (!isRecord(raw)) return undefined;
  const kind = oneOf(raw.kind, ANSWER_KINDS);
  if (kind === undefined) return undefined;
  return {
    kind,
    value: raw.value,
    answeredByUserId: optionalStr(raw.answered_by_user_id),
    ...(raw.answered_by_givmo_desk === true ? { answeredByGivmoDesk: true } : {}),
    answeredAt: optionalStr(raw.answered_at),
  };
}

/** One numbered thing the form asks, in the form's own words. */
export interface FormItem {
  key: string;
  number: string;
  label: string;
  kind: FormItemKind;
  required: boolean;
  state: FormItemState;
  /** The form requires an answer and none stands yet — the server's word, never re-derived. */
  waiting: boolean;
  places: FormPlace[];
  /** What goes on the form for it, one string per line or column. */
  values: string[];
  choice?: string;
  choices: FormChoice[];
  routes: FormRoute[];
  /** What "Doesn't apply" writes there, and the form's instruction that says so. */
  doesntApply?: { writes: string; basis?: FormQuote };
  instruction?: FormQuote;
  /** The item whose written value this one may copy ("Same as the mailing address"). */
  sameAs?: string;
  /**
   * The records the form fills it from, as declared, whatever they hold now: what tells an
   * unrecorded fiscal year end is the organization's (CCM-R1-DOCS-READS). `sources` are those that
   * hold a value.
   */
  reads: FormSourceRef[];
  sources: FormSource[];
  /** Two records that differ, offered for the organization's choice. */
  candidates: FormSource[];
  answer?: FormAnswer;
  /** The served line saying why the value cannot go on the form as it is. */
  problem?: string;
}

function decodeItem(raw: unknown): FormItem | undefined {
  if (!isRecord(raw)) return undefined;
  const key = optionalStr(raw.key);
  if (!key) return undefined;
  const places = Array.isArray(raw.places)
    ? raw.places.flatMap((place) => {
        if (!isRecord(place)) return [];
        const page = optionalInt(place.page);
        const box = rect(place.rect);
        return page !== undefined && box !== undefined ? [{ page, rect: box }] : [];
      })
    : [];
  const choices = Array.isArray(raw.choices)
    ? raw.choices.flatMap((choice) => {
        if (!isRecord(choice)) return [];
        const choiceKey = optionalStr(choice.key);
        const label = optionalStr(choice.label);
        return choiceKey && label ? [{ key: choiceKey, label }] : [];
      })
    : [];
  const routes = Array.isArray(raw.routes)
    ? raw.routes.flatMap((route) => {
        if (!isRecord(route)) return [];
        const routeKey = optionalStr(route.key);
        const writes = optionalStr(route.writes);
        return routeKey && writes ? [{ key: routeKey, writes }] : [];
      })
    : [];
  const doesnt = isRecord(raw.doesnt_apply) ? raw.doesnt_apply : undefined;
  const reads = Array.isArray(raw.reads)
    ? raw.reads.flatMap((ref) => {
        const decoded = decodeSourceRef(ref);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
  const sources = Array.isArray(raw.sources)
    ? raw.sources.flatMap((source) => {
        const decoded = decodeSource(source);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
  const candidates = Array.isArray(raw.candidates)
    ? raw.candidates.flatMap((source) => {
        const decoded = decodeSource(source);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
  return {
    key,
    number: str(raw.number),
    label: str(raw.label),
    kind: oneOf(raw.kind, ITEM_KINDS) ?? 'unknown',
    required: raw.required === true,
    state: oneOf(raw.state, ITEM_STATES) ?? 'unknown',
    waiting: raw.waiting === true,
    places,
    values: Array.isArray(raw.values) ? raw.values.map(str) : [],
    choice: optionalStr(raw.choice),
    choices,
    routes,
    doesntApply:
      doesnt !== undefined && typeof doesnt.writes === 'string'
        ? { writes: doesnt.writes, basis: decodeQuote(doesnt.basis) }
        : undefined,
    instruction: decodeQuote(raw.instruction),
    sameAs: optionalStr(raw.same_as),
    reads,
    sources,
    candidates,
    answer: decodeAnswer(raw.answer),
    problem: optionalStr(raw.problem),
  };
}

/** How the preview draws one page (CCM-R1-DOCS-READS): its scale, crop box and rotation. */
export interface FormPage {
  page: number;
  scale: number;
  cropBox: Rect;
  rotation: number;
}

// ── What the form prints beside its items (CCM-R1-FORM-CATALOG; CCM-R1-DOCS-PRINTED-WEB) ──────

/** The page of the agency's document that holds the form's checklist, and the form's own sentence
 * saying so. */
export interface FormChecklistPage {
  page: number;
  basis?: FormQuote;
}

/** A question the form prints over several of its items ("14. Does your organization …?"): the
 * form's number for it, the items it holds in the form's order, and the question in its words. */
export interface FormGroup {
  number: string;
  itemKeys: string[];
  question: FormQuote;
}

/** The form's fee chart as the form prints it: its column headings, each row's cells under them
 * (one per heading, a typo included), and its notes, each in the form's words. */
export interface FormFeeChart {
  page: number;
  headings: string[];
  rows: string[][];
  notes: FormQuote[];
}

function decodeChecklistPage(raw: unknown): FormChecklistPage | undefined {
  if (!isRecord(raw)) return undefined;
  const page = optionalInt(raw.page);
  if (page === undefined || page < 1) return undefined;
  const basis = decodeQuote(raw.basis);
  return basis !== undefined ? { page, basis } : { page };
}

/** The served groups over the items the read carries: an item belongs to one group (the first
 * that names it), and a group holding fewer than two of the items is no group. */
function decodeGroups(raw: unknown, items: readonly FormItem[]): FormGroup[] {
  if (!Array.isArray(raw)) return [];
  const known = new Set(items.map((item) => item.key));
  const claimed = new Set<string>();
  return raw.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const question = decodeQuote(entry.question);
    const itemKeys = Array.isArray(entry.item_keys)
      ? entry.item_keys.filter(
          (key): key is string => typeof key === 'string' && known.has(key) && !claimed.has(key),
        )
      : [];
    if (question === undefined || new Set(itemKeys).size < 2) return [];
    const keys = [...new Set(itemKeys)];
    for (const key of keys) claimed.add(key);
    return [{ number: str(entry.number), itemKeys: keys, question }];
  });
}

/** A fee chart row read as one cell under each heading, or nothing where it isn't exactly that. */
function decodeFeeChartRow(row: unknown, width: number): string[] | undefined {
  if (!isRecord(row) || !Array.isArray(row.cells)) return undefined;
  const cells: unknown[] = row.cells;
  return cells.length === width && cells.every((cell) => typeof cell === 'string')
    ? (cells as string[])
    : undefined;
}

/**
 * The fee chart, its rows read as one cell under each heading (CCM-R1-FORM-CATALOG-B's shape), all
 * or nothing: one row that isn't exactly that — an older `{level, fee}` row among them — makes the
 * chart unreadable, and an unreadable chart draws nothing rather than a partial one. A chart missing
 * a band would misstate the form's own (review-62, P1).
 */
export function decodeFeeChart(raw: unknown): FormFeeChart | undefined {
  if (!isRecord(raw) || !Array.isArray(raw.headings) || !Array.isArray(raw.rows)) return undefined;
  const page = optionalInt(raw.page);
  const headings = raw.headings.filter(
    (heading): heading is string => typeof heading === 'string' && heading.length > 0,
  );
  if (page === undefined || page < 1 || headings.length < 2) return undefined;
  if (headings.length !== raw.headings.length) return undefined;
  const rows = raw.rows.map((row) => decodeFeeChartRow(row, headings.length));
  if (rows.length === 0 || !rows.every((row): row is string[] => row !== undefined)) {
    return undefined;
  }
  const notes = Array.isArray(raw.notes)
    ? raw.notes.flatMap((note) => {
        const decoded = decodeQuote(note);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
  return { page, headings, rows, notes };
}

/** One immutable version of a filing document, as the wire describes it. */
export interface DocumentVersion {
  versionId: string;
  documentId: string;
  versionNumber?: number;
  kind?: string;
  contentSha256: string;
  pageCount?: number;
  sourceArtifactId?: string;
  /**
   * What it was made from: a signed form's final names the version its signers signed, and a copy
   * signed outside Givmo the version it executes.
   */
  derivedFromVersionId?: string;
  createdByUserId?: string;
  /** Givmo's filing desk made it (D-CCM-70's marker; CCM-R1-DESK-WEB). */
  createdByGivmoDesk?: boolean;
  createdAt?: string;
  /**
   * CCM-R1-SIGN-OUTSIDE: a copy signed outside Givmo (`executed_copy`) carries how it was signed, as
   * the organization states, and the statement every view of it shows. Absent on every other kind.
   */
  execution?: DocumentExecution;
}

/** How a copy signed outside Givmo was signed, as the organization states (the served block). */
export interface DocumentExecution {
  signers: { name: string; capacity: string; signedOn: string }[];
  /** W7 as the backend serves it on every copy. */
  statement: string;
}

/**
 * The `execution` block, read tolerantly: a signer missing a field is dropped, and a block with no
 * signer or no statement is not read at all (a copy is never shown without its statement).
 */
function decodeExecution(raw: unknown): DocumentExecution | undefined {
  if (!isRecord(raw) || !Array.isArray(raw.signers)) return undefined;
  const signers = raw.signers.flatMap((signer) => {
    if (!isRecord(signer)) return [];
    const name = optionalStr(signer.name);
    const capacity = optionalStr(signer.capacity);
    const signedOn = optionalStr(signer.signed_on);
    return name && capacity && signedOn ? [{ name, capacity, signedOn }] : [];
  });
  const statement = optionalStr(raw.statement);
  return signers.length > 0 && statement ? { signers, statement } : undefined;
}

export function decodeDocumentVersion(raw: unknown): DocumentVersion | undefined {
  if (!isRecord(raw)) return undefined;
  const versionId = optionalStr(raw.version_id);
  const documentId = optionalStr(raw.document_id);
  const contentSha256 = optionalStr(raw.content_sha256);
  if (!versionId || !documentId || !contentSha256) return undefined;
  const execution = decodeExecution(raw.execution);
  return {
    versionId,
    documentId,
    versionNumber: optionalInt(raw.version_number),
    kind: optionalStr(raw.kind),
    contentSha256,
    pageCount: optionalInt(raw.page_count),
    sourceArtifactId: optionalStr(raw.source_artifact_id),
    derivedFromVersionId: optionalStr(raw.derived_from_version_id),
    createdByUserId: optionalStr(raw.created_by_user_id),
    ...(raw.created_by_givmo_desk === true ? { createdByGivmoDesk: true } : {}),
    createdAt: optionalStr(raw.created_at),
    ...(execution !== undefined ? { execution } : {}),
  };
}

/** A form Givmo prepares for one filing case. */
export interface FormPreparation {
  preparationId: string;
  filingCaseId: string;
  documentId: string;
  adapterKey: string;
  formNumber: string;
  shortName?: string;
  items: FormItem[];
  pages: FormPage[];
  /** The title the form prints ("Registration Statement for Charitable Organizations"). */
  printedTitle?: FormQuote;
  /** The page holding the form's checklist ("See the checklist (page 2)"). */
  checklistPage?: FormChecklistPage;
  /** The questions the form prints over several items (COR-92's item 14). */
  groups: FormGroup[];
  /** The form's own fee chart, as it prints it. */
  feeChart?: FormFeeChart;
  waitingCount: number;
  complete: boolean;
  /** Binds everything the form says; a version is recorded only against the one read. */
  valuesSha256: string;
  /** Out for signature: answers and new versions wait. */
  locked: boolean;
  latestVersion?: DocumentVersion;
  latestVersionValuesSha256?: string;
  /**
   * CCM-R1-SIGN-OUTSIDE-SEND (P4): the version that executes the form as it stands, as the server
   * reads it (`current_execution`): Givmo's signed final of its newest version, else the newest copy
   * of it signed outside Givmo, else `null`. A signature of an earlier version is never the form's.
   * `undefined` only where the server predates the field (its reading then stays the request's).
   */
  currentExecution?: DocumentVersion | null;
  /**
   * CCM-R1-SIGN-OUTSIDE-COMPARE-PAGE: the pages the form's signature places are on, in the order
   * the form declares them (`signature_pages`); the comparison of a signed copy opens on the first.
   * `undefined` where the server predates the field.
   */
  signaturePages?: number[];
  createdByUserId?: string;
  /** Givmo's filing desk started it (D-CCM-70's marker; CCM-R1-DESK-WEB). */
  createdByGivmoDesk?: boolean;
  createdAt?: string;
}

export function decodeFormPreparation(body: unknown): FormPreparation | undefined {
  if (!isRecord(body)) return undefined;
  const preparationId = optionalStr(body.preparation_id);
  const filingCaseId = optionalStr(body.filing_case_id);
  const documentId = optionalStr(body.document_id);
  const adapterKey = optionalStr(body.adapter_key);
  const valuesSha256 = optionalStr(body.values_sha256);
  if (!preparationId || !filingCaseId || !documentId || !adapterKey || !valuesSha256) {
    return undefined;
  }
  const items = Array.isArray(body.items)
    ? body.items.flatMap((raw) => {
        const decoded = decodeItem(raw);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
  const pages = Array.isArray(body.pages)
    ? body.pages.flatMap((raw) => {
        if (!isRecord(raw)) return [];
        const page = optionalInt(raw.page);
        const scale = optionalNumber(raw.scale);
        const cropBox = rect(raw.crop_box);
        if (page === undefined || scale === undefined || scale <= 0 || cropBox === undefined) {
          return [];
        }
        return [{ page, scale, cropBox, rotation: optionalInt(raw.rotation) ?? 0 }];
      })
    : [];
  return {
    preparationId,
    filingCaseId,
    documentId,
    adapterKey,
    formNumber: str(body.form_number) || adapterKey,
    shortName: optionalStr(body.short_name),
    items,
    pages,
    printedTitle: decodeQuote(body.printed_title),
    checklistPage: decodeChecklistPage(body.checklist),
    groups: decodeGroups(body.groups, items),
    feeChart: decodeFeeChart(body.fee_chart),
    waitingCount: optionalInt(body.waiting_count) ?? items.filter((item) => item.waiting).length,
    complete: body.complete === true,
    valuesSha256,
    locked: body.locked === true,
    latestVersion: decodeDocumentVersion(body.latest_version),
    latestVersionValuesSha256: optionalStr(body.latest_version_values_sha256),
    ...('current_execution' in body
      ? { currentExecution: decodeDocumentVersion(body.current_execution) ?? null }
      : {}),
    ...(Array.isArray(body.signature_pages)
      ? {
          signaturePages: body.signature_pages.flatMap((raw) => {
            const page = optionalInt(raw);
            return page !== undefined && page >= 1 ? [page] : [];
          }),
        }
      : {}),
    createdByUserId: optionalStr(body.created_by_user_id),
    ...(body.created_by_givmo_desk === true ? { createdByGivmoDesk: true } : {}),
    createdAt: optionalStr(body.created_at),
  };
}

/** A recorded version of the prepared form, and whether this request recorded it. */
export interface PreparedVersion {
  version: DocumentVersion;
  valuesSha256: string;
  complete: boolean;
}

export function decodePreparedVersion(body: unknown): PreparedVersion | undefined {
  if (!isRecord(body)) return undefined;
  const version = decodeDocumentVersion(body.version);
  const valuesSha256 = optionalStr(body.values_sha256);
  if (version === undefined || !valuesSha256) return undefined;
  return { version, valuesSha256, complete: body.complete === true };
}

/** A short-lived link to one version's exact bytes. */
export function decodeVersionDownloadUrl(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined;
  const url = optionalStr(body.download_url);
  return url !== undefined && /^https?:\/\//.test(url) ? url : undefined;
}

// ── The checklist ──────────────────────────────────────────────────────────────────────────────

export type ChecklistStatus = 'open' | 'attached' | 'not_needed';

export interface ChecklistDocument {
  documentId: string;
  title: string;
  latestVersion?: DocumentVersion;
}

export interface ChecklistAct {
  act: 'attach' | 'remove' | 'not_needed' | 'needed';
  documentId?: string;
  recordedByUserId?: string;
  /** Givmo's filing desk recorded it (D-CCM-70's marker; CCM-R1-DESK-WEB). */
  recordedByGivmoDesk?: boolean;
  recordedAt?: string;
}

/** One item of the filing's attachment checklist, in the rule's own words. */
export interface ChecklistItem {
  attachmentIndex: number;
  item: string;
  condition?: string;
  /** The agency's own words beside the item (Maryland's Schedule B sentence, D-CCM-24 J3). */
  agencyQuote?: FormQuote;
  status: ChecklistStatus;
  documents: ChecklistDocument[];
  lastAct?: ChecklistAct;
}

export function decodeChecklist(body: unknown): ChecklistItem[] {
  if (!isRecord(body) || !Array.isArray(body.items)) return [];
  return body.items.flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const attachmentIndex = optionalInt(raw.attachment_index);
    const item = optionalStr(raw.item);
    const status = oneOf(raw.status, ['open', 'attached', 'not_needed'] as const);
    if (attachmentIndex === undefined || !item || status === undefined) return [];
    const documents = Array.isArray(raw.documents)
      ? raw.documents.flatMap((document) => {
          if (!isRecord(document)) return [];
          const documentId = optionalStr(document.document_id);
          if (!documentId) return [];
          return [
            {
              documentId,
              title: str(document.title),
              latestVersion: decodeDocumentVersion(document.latest_version),
            },
          ];
        })
      : [];
    const last = isRecord(raw.last_act) ? raw.last_act : undefined;
    const act =
      last !== undefined
        ? oneOf(last.act, ['attach', 'remove', 'not_needed', 'needed'] as const)
        : undefined;
    return [
      {
        attachmentIndex,
        item,
        condition: optionalStr(raw.condition),
        agencyQuote: decodeQuote(raw.agency_quote),
        status,
        documents,
        lastAct:
          last !== undefined && act !== undefined
            ? {
                act,
                documentId: optionalStr(last.document_id),
                recordedByUserId: optionalStr(last.recorded_by_user_id),
                ...(last.recorded_by_givmo_desk === true ? { recordedByGivmoDesk: true } : {}),
                recordedAt: optionalStr(last.recorded_at),
              }
            : undefined,
      },
    ];
  });
}

// ── The case's signing requests (read only, here) ──────────────────────────────────────────────

export type SigningRequestState = 'open' | 'completed' | 'cancelled' | 'expired' | 'superseded';

export interface SigningRequestRow {
  requestId: string;
  filingCaseId: string;
  state: SigningRequestState | 'unknown';
  expiresAt?: string;
  createdAt?: string;
  completedAt?: string;
  issuedByUserId?: string;
}

export function extractSigningRequests(body: unknown): SigningRequestRow[] {
  return extractList<unknown>(body).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const requestId = optionalStr(raw.request_id);
    const filingCaseId = optionalStr(raw.filing_case_id);
    if (!requestId || !filingCaseId) return [];
    return [
      {
        requestId,
        filingCaseId,
        state:
          oneOf(raw.state, ['open', 'completed', 'cancelled', 'expired', 'superseded'] as const) ??
          'unknown',
        expiresAt: optionalStr(raw.expires_at),
        createdAt: optionalStr(raw.created_at),
        completedAt: optionalStr(raw.completed_at),
        issuedByUserId: optionalStr(raw.issued_by_user_id),
      },
    ];
  });
}

/** Who a request is out with (its places' signers, in order, once each), as its read names them. */
export interface SigningRequestSigners {
  state: SigningRequestState | 'unknown';
  signerUserIds: string[];
  /** The versions the request sends for signature (its documents'), for P4's stale-read rule. */
  documentVersionIds: string[];
  completedAt?: string;
  expiresAt?: string;
}

export function decodeSigningRequestSigners(body: unknown): SigningRequestSigners | undefined {
  if (!isRecord(body) || !isRecord(body.request)) return undefined;
  const request = body.request;
  const signerUserIds: string[] = [];
  if (Array.isArray(body.slots)) {
    for (const slot of body.slots) {
      if (!isRecord(slot)) continue;
      const signer = optionalStr(slot.signer_user_id);
      if (signer !== undefined && !signerUserIds.includes(signer)) signerUserIds.push(signer);
    }
  }
  const documentVersionIds = Array.isArray(body.documents)
    ? body.documents.flatMap((document) => {
        const versionId = isRecord(document)
          ? optionalStr(document.document_version_id)
          : undefined;
        return versionId !== undefined ? [versionId] : [];
      })
    : [];
  return {
    state:
      oneOf(request.state, ['open', 'completed', 'cancelled', 'expired', 'superseded'] as const) ??
      'unknown',
    signerUserIds,
    documentVersionIds,
    completedAt: optionalStr(request.completed_at),
    expiresAt: optionalStr(request.expires_at),
  };
}
